/**
 * Resolve the active CHF/m² band for Apt or House from backend market fields.
 * Does NOT invent prices — missing type fields stay null (backend is SSOT).
 */
export function resolveTypeBand(result, propertyType) {
  if (!result || result.status !== 'ok') {
    return { mid: null, min: null, max: null };
  }
  const type = propertyType === 'house' ? 'house' : 'apartment';
  const mid = type === 'house'
    ? (result.house_price ?? null)
    : (result.apartment_price ?? null);
  let min = type === 'house'
    ? (result.house_price_min ?? null)
    : (result.apartment_price_min ?? null);
  let max = type === 'house'
    ? (result.house_price_max ?? null)
    : (result.apartment_price_max ?? null);

  // Fallback only to top-level active fields when they match the requested type
  // or when dual fields are absent but top-level exists for the fetch-time type.
  if (mid == null) {
    const topType = (result.property_type || '').toLowerCase();
    if (!topType || topType === type) {
      return {
        mid: result.price_per_sqm ?? null,
        min: result.price_min ?? null,
        max: result.price_max ?? null,
      };
    }
    return { mid: null, min: null, max: null };
  }

  if (min == null || max == null) {
    min = min ?? result.price_min ?? null;
    max = max ?? result.price_max ?? null;
  }
  if (min != null && max != null && min > max) {
    const tmp = min;
    min = max;
    max = tmp;
  }
  return { mid, min, max };
}

/**
 * Street / location evaluation band for the simplified Market Reference UI.
 * Mid prefers street_average; range is the envelope of apt/house (or top-level) bands.
 * Does not invent a mid when the backend did not provide one.
 */
export function resolveStreetBand(result) {
  if (!result || result.status !== 'ok') {
    return { mid: null, min: null, max: null };
  }
  const apt = resolveTypeBand(result, 'apartment');
  const house = resolveTypeBand(result, 'house');
  const mid = result.street_average
    ?? result.price_per_sqm
    ?? apt.mid
    ?? house.mid
    ?? null;

  const mins = [apt.min, house.min, result.price_min].filter((v) => v != null && Number.isFinite(Number(v)));
  const maxs = [apt.max, house.max, result.price_max].filter((v) => v != null && Number.isFinite(Number(v)));
  let min = mins.length ? Math.min(...mins.map(Number)) : null;
  let max = maxs.length ? Math.max(...maxs.map(Number)) : null;

  if (min != null && max != null && min > max) {
    const tmp = min;
    min = max;
    max = tmp;
  }
  // Single-point band when only mid exists
  if (mid != null && min == null && max == null) {
    min = mid;
    max = mid;
  }
  return { mid, min, max };
}

/**
 * Project dual-band backend payload onto the active type fields used by cache consumers
 * (QuickTest reads marketData.price_per_sqm).
 */
export function projectActiveType(result, propertyType) {
  if (!result || result.status !== 'ok') return result;
  const type = propertyType === 'house' ? 'house' : 'apartment';
  const band = resolveTypeBand(result, type);
  if (band.mid == null) return { ...result, property_type: type };
  return {
    ...result,
    property_type: type,
    price_per_sqm: band.mid,
    price_min: band.min,
    price_max: band.max,
  };
}
